// Peças comuns do Diário (0.65).
import { useMemo, useState, type ReactNode } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { AbaId } from '../../store/types';
import { getDoc } from '../../store/store';
import { mudarDiario } from '../../store/diario';
import { useAcoes } from '../Acoes';
import { ScrollArrastavel } from '../Arrastar';
import { PaginaRolavel } from '../PaginaEditor';
import { C, alpha } from '../theme';
import { MES_CURTO, nomeMes, parseYmd, posNaSemana, today, ymd } from '../../lib/date';
import { N_, t } from '../../lib/i18n';
import { useCaixas } from '../../lib/caixas';
import { Recolhivel } from '../Recolhivel';

export const COR_DIARIO = '#C2185B';
export const HUMORES: [number, string, string][] = [[1, '😢', N_('Muito mal')], [2, '😕', N_('Mal')], [3, '😐', N_('Normal')], [4, '🙂', N_('Bem')], [5, '😄', N_('Muito bem')], [6, '🤩', N_('Incrível')]];
export const emojiHumor = (n?: number | null) => HUMORES.find(h => h[0] === n)?.[1] ?? '';

export type Parte = 'dia' | 'vida' | 'habitos' | 'sonhos' | 'roda' | 'cartas' | 'insp' | 'conquistas' | 'grupo';
export const PASTAS: { id: Exclude<Parte, 'dia'>; nome: string; icone: string; desc: string }[] = [
  { id: 'vida', nome: N_('Projeto de Vida'), icone: '🧭', desc: N_('Metas, objetivos e sonhos, com passos e prazos') },
  { id: 'habitos', nome: N_('Hábitos'), icone: '🔥', desc: N_('Marque o que fez em cada dia') },
  { id: 'sonhos', nome: N_('Quadro dos Sonhos'), icone: '🌠', desc: N_('Mural de fotos dos seus sonhos') },
  { id: 'roda', nome: N_('Roda da Vida'), icone: '🎡', desc: N_('Nota de 0 a 10 para cada área da vida') },
  { id: 'cartas', nome: N_('Cartas para o Futuro'), icone: '💌', desc: N_('Escreva hoje, abra no futuro') },
  { id: 'insp', nome: N_('Inspiração'), icone: '✨', desc: N_('Frases, textos, vídeos e fotos que motivam') },
  { id: 'conquistas', nome: N_('Conquistas'), icone: '🏆', desc: N_('Mural, relatório e revisão do mês') },
  { id: 'grupo', nome: N_('Projetos em Grupo'), icone: '🤝', desc: N_('Projetos com amigos e família (compartilhados)') },
];

/** Barra de progresso. */
export function Barra({ p, cor, alt = 8 }: { p: number; cor: string; alt?: number }) {
  return (
    <View style={{ height: alt, borderRadius: alt / 2, backgroundColor: C.card2, overflow: 'hidden' }}>
      <View style={{ width: `${Math.round(Math.max(0, Math.min(1, p)) * 100)}%`, height: alt, backgroundColor: cor }} />
    </View>
  );
}

/** Parte que abre e fecha tocando no título (o componente único do app, components/Recolhivel.tsx, com a seta à esquerda como sempre foi no Diário). */
export function Recolhe({ titulo, children, aberto = false, cor }: { titulo: string; children: ReactNode; aberto?: boolean; cor?: string }) {
  const cx = useCaixas();
  return (
    <Recolhivel visual="cartao" seta="esquerda" titulo={titulo} inicial={aberto} cor={cor} gap={cx.gap(8)} linhas={0}
      estiloBarra={{ paddingHorizontal: cx.padH(12) }} estiloTitulo={{ letterSpacing: 0 }}>
      {children}
    </Recolhivel>
  );
}

/** Enviar um item do Diário para outra aba: vai SÓ o título e o item continua ligado ao Diário. */
export function useEnviarDiario() {
  const { enviar } = useAcoes();
  return (chave: string, titulo: string, emoji?: string, data?: string | null) => enviar({
    titulo, emoji, origem: 'diario' as AbaId, data: data ?? null, soTitulo: true,
    link: { aba: 'diario', id: chave, label: titulo },
    onEnviado: d => mudarDiario(x => ({ ...x, enviados: { ...(x.enviados ?? {}), [chave]: { titulo, aba: d.aba, em: today(), ...(d.tarefa ? { tarefa: d.tarefa } : {}), ...(d.caixa ? { caixa: d.caixa } : {}) } } })),
  });
}

/** O item foi concluído em outra aba? (tarefa marcada no My Planner ou o título marcado na caixa recebida) */
export function concluidoFora(chave: string): string | null {
  const e = getDoc('diariop').enviados?.[chave];
  if (!e) return null;
  if (e.tarefa) {
    const tk = getDoc('planner').tasks.find(x => x.id === e.tarefa);
    if (tk && tk.recur.type === 'none' && tk.done.length) return [...tk.done].sort().pop()!;
  }
  if (e.caixa) {
    const b = getDoc('anotacoes').boxes.find(x => x.id === e.caixa);
    const its = b?.blocks.flatMap(x => x.items) ?? [];
    if (its.length && its.every(i => i.done)) return today();
  }
  return null;
}
export const tarefaDe = (chave: string) => getDoc('diariop').enviados?.[chave]?.tarefa;

/** Calendário do mês com os dias marcados (dias escritos, dias cumpridos...). */
export function CalendarioMes({ data, marcados, cor, onDia, pequeno }: { data: string; marcados: (d: string) => boolean; cor: string; onDia?: (d: string) => void; pequeno?: boolean }) {
  const base = parseYmd(data);
  const [mes, setMes] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const dias = useMemo(() => {
    const ini = new Date(mes.y, mes.m, 1);
    const off = posNaSemana(ini.getDay());
    const n = new Date(mes.y, mes.m + 1, 0).getDate();
    return [...Array(off).fill(null), ...Array.from({ length: n }, (_, i) => ymd(new Date(mes.y, mes.m, i + 1)))] as (string | null)[];
  }, [mes]);
  const hoje = today();
  const passo = (n: number) => setMes(m => { const d = new Date(m.y, m.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const tam = pequeno ? 26 : 38;
  return (
    <View style={{ gap: 6 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Pressable onPress={() => passo(-1)} hitSlop={10} style={{ padding: 4 }}><Text style={{ color: C.text, fontSize: 18 }}>‹</Text></Pressable>
        <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '700', fontSize: pequeno ? 12 : 15 }}>{nomeMes(mes.m)} {mes.y}</Text>
        <Pressable onPress={() => passo(1)} hitSlop={10} style={{ padding: 4 }}><Text style={{ color: C.text, fontSize: 18 }}>›</Text></Pressable>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {dias.map((d, i) => {
          const on = !!d && marcados(d);
          return (
            <Pressable key={i} disabled={!d || !onDia} onPress={() => d && onDia?.(d)} style={{ width: `${100 / 7}%`, height: tam, alignItems: 'center', justifyContent: 'center' }}>
              {!!d && (
                <View style={{ width: tam - 6, height: tam - 6, borderRadius: (tam - 6) / 2, alignItems: 'center', justifyContent: 'center',
                  backgroundColor: on ? cor : d === hoje ? alpha(cor, 0.18) : 'transparent', borderWidth: d === data ? 2 : 0, borderColor: cor }}>
                  <Text style={{ color: on ? '#fff' : C.text, fontSize: pequeno ? 10 : 13, fontWeight: on ? '800' : '400' }}>{Number(d.slice(8))}</Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

export const dataLonga = (d: string) => { const x = parseYmd(d); return `${x.getDate()} ${t(MES_CURTO[x.getMonth()])} ${x.getFullYear()}`; };

/** Conteúdo rolável dentro das folhas (Sheet) do Diário (texto 12: a barra do editor fica fixa no topo, como em Estudos). */
export function Corpo({ children }: { children: ReactNode }) {
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  return (
    <PaginaRolavel editor Rolagem={ScrollArrastavel} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: cx.gap(10), paddingBottom: 120 }}>
      {children}
    </PaginaRolavel>
  );
}
